import { useEffect, useState } from 'react';

import { Loader, Modal } from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';

import { logger } from '../../../common/logger';
import { getEmail, getUsername } from '../../../common/utilities';
import * as types from '../../../common/utilities/constants';
import FormRequestInfoPage from '../FormRequestInfoPage';
import FormSubmissionPage from '../FormSubmissionPage';

import './styles/index.css';

const RequestTechnicalDocument = props => {
  const { apiUrls, Post, handleCloseModal, selectedAccount, selectedRegion, fromMyRequests } = props;

  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const setUserDetails = async () => {
    try {
      const { auth } = props;
      const uEmail = await getEmail(auth);
      const uName = await getUsername(auth);
      setUserEmail(uEmail);
      setUserName(uName);
    } catch (err) {
      logger.error('Could not get userEmail', err);
    }
  };

  useEffect(() => {
    setUserDetails();
  }, []);

  const handleReturnClick = () => {
    setFormSubmitted(false);
  };

  const convertPayloadToFile = payload => new Blob([JSON.stringify(payload)], { type: 'application/json' });

  const getDocumentRequestHeaders = () => {
    const headers = {};
    headers['Content-Type'] = 'multipart/form-data';

    return headers;
  };

  const conditionalResponse = (condition, whenConditionIsTrue, whenConditionIsFalse) =>
    condition ? whenConditionIsTrue : whenConditionIsFalse;

  const handleSubmit = values => {
    const { facility, requestDescription, detailOfRequest } = values;
    setLoading(true);
    let analyticsData = {};
    if (fromMyRequests) {
      analyticsData = {
        capability: types.MY_REQUESTS_CAPABILITY,
        element: { group: types.MY_REQUESTS_REQ_TECH_ELEMENT_GROUP },
        description: types.MY_REQUESTS_REQ_TECH_DOC_DESCP,
        event: types.MY_REQUESTS_PROCESS_COMPLETE,
        pageName: types.MY_REQUESTS_REQ_TECH_BASE_URL.replace('{##}', 'complete')
      };
    } else {
      analyticsData = {
        capability: types.REQUEST_TECH_DOC_CAPABILITY,
        event: types.REQUEST_TECH_DOC_COMPLETE_EVENT,
        description: types.REQUEST_TECH_DOC_DESCP,
        pageName: types.REQUEST_TECH_DOC_COMPLETE_EVENT_URL,
        form: { transactionID: '' }
      };
    }
    const [file = { name: '' }] = selectedFiles;
    const payload = {
      detailOfRequest: `${detailOfRequest.replaceAll('\n', ' ')}\n{#*#}`,
      requestDescription,
      cargillFacility: facility,
      contactName: userName,
      contactEmail: userEmail,
      region: selectedRegion.shortname,
      accountId: selectedAccount.globalId,
      accountName: selectedAccount.fullname,
      fileName: conditionalResponse(file, file.name, '')
    };

    const formData = new FormData();
    formData.append('file', file);
    formData.append('payload', convertPayloadToFile(payload));

    Post(apiUrls.techDocReqUrl, formData, true, getDocumentRequestHeaders())
      .then(res => {
        if (res.data) {
          analyticsData = { ...analyticsData, form: { transactionID: res.data } };
        }
        setIsSuccess(true);
        setFormSubmitted(true);
        setLoading(false);
        setSelectedFiles([]);
        const analyticsEvent = conditionalResponse(fromMyRequests, types.MY_REQUESTS_ANALYTICS, types.DOCUMENTS_EVENT);
        PubSub.publish(analyticsEvent, analyticsData);
      })
      .catch(e => {
        if (fromMyRequests) {
          analyticsData = {
            ...analyticsData,
            event: types.MY_REQUESTS_PROCESS_ERROR,
            pageName: types.MY_REQUESTS_REQ_TECH_BASE_URL.replace('{##}', 'error'),
            description: types.MY_REQUESTS_REQ_TECH_PROCESS_ERROR_DESCP
          };
        } else {
          analyticsData = {
            ...analyticsData,
            event: types.REQUEST_TECH_DOC_UPLOAD_FAILED_EVENT,
            pageName: types.REQUEST_TECH_DOC_UPLOAD_FAILED_URL,
            description: types.REQUEST_TECH_DOC_UPLOAD_FAILED_DESC
          };
        }
        logger.warn(`Tech doc request failed: ${e}`);
        setIsSuccess(false);
        setFormSubmitted(true);
        setLoading(false);
        setSelectedFiles([]);
        const analyticsEvent = fromMyRequests ? types.MY_REQUESTS_ANALYTICS : types.DOCUMENTS_EVENT;
        PubSub.publish(analyticsEvent, analyticsData);
      });
  };

  const onFileSelect = filesSelected => {
    let analyticsData = {
      capability: types.REQUEST_TECH_DOC_CAPABILITY,
      event: types.REQUEST_TECH_DOC_UPLOAD_COMPLETE_EVENT,
      description: types.REQUEST_TECH_DOC_UPLOAD_COMPLETE_DESCP,
      pageName: types.REQUEST_TECH_DOC_UPLOAD_EVENT_URL
    };
    if (fromMyRequests) {
      analyticsData = {
        element: { group: types.MY_REQUESTS_REQ_TECH_ELEMENT_GROUP },
        capability: types.MY_REQUESTS_CAPABILITY,
        event: types.MY_REQUESTS_DOCUMENT_UPLOAD,
        description: types.MY_REQUESTS_REQ_TECH_DOCUMENT_UPLOAD_DESCP,
        pageName: types.MY_REQUESTS_REQ_TECH_BASE_URL.replace('{##}', 'upload document')
      };
    }
    setSelectedFiles(filesSelected);

    PubSub.publish(types.DOCUMENTS_EVENT, analyticsData);
  };

  const onRemoveFile = ({ name }) => {
    const remainingFiles = selectedFiles.filter(({ name: fileName }) => fileName !== name);
    setSelectedFiles(remainingFiles);
  };

  return (
    <Modal handleCloseModal={handleCloseModal}>
      <div className="request-document-container">
        {!formSubmitted ? (
          <FormRequestInfoPage
            {...props}
            onRemoveFile={onRemoveFile}
            onFileSelect={onFileSelect}
            onSubmit={handleSubmit}
            selectedFiles={selectedFiles}
          />
        ) : (
          <FormSubmissionPage {...props} isSuccess={isSuccess} onReturn={handleReturnClick} />
        )}
        {loading && <Loader isLayoutLoader={loading} />}
      </div>
    </Modal>
  );
};

RequestTechnicalDocument.propTypes = {
  labels: PropTypes.instanceOf(Object).isRequired,
  auth: PropTypes.instanceOf(Object).isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  Get: PropTypes.func.isRequired,
  Post: PropTypes.func.isRequired,
  apiUrls: PropTypes.instanceOf(Object).isRequired,
  selectedRegion: PropTypes.instanceOf(Object).isRequired,
  selectedAccount: PropTypes.instanceOf(Object).isRequired,
  fromMyRequests: PropTypes.bool
};

RequestTechnicalDocument.defaultProps = {
  fromMyRequests: false
};

export default RequestTechnicalDocument;
